import { DisplayText, Card, Spinner } from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';
import React, { useEffect, useMemo, useState } from 'react';
import { SVGIcon } from '@superops/super-icons';
import { gql, useLazyQuery } from '@apollo/client';
import { AccountClient, GET_AGGRGATED_VALUES } from '@nile/graphql';
import { RuleConditionInput } from '@nile/types';
import { CurrencyText, EventEmitterWithIndex } from '@nile/components';

interface MetricSource {
  fieldName: string;
  argumentName: string;
  argumentType: string;
}

interface metricType {
  metricFunction: 'COUNT';
  attribute?: string;
  metricSource?: MetricSource;
  filterCondition: RuleConditionInput;
}

interface WidgetProps {
  entity: string;
  label: string;
  icon: string;
  metric: metricType;
  additionalFilterConditions?: RuleConditionInput[];
  unitType?: string;
}

const getDynamicWidgetQuery = ({
  querySource,
  filterCondition,
  additionalFilterConditions
}: {
  querySource: MetricSource;
  filterCondition: RuleConditionInput;
  additionalFilterConditions?: RuleConditionInput[];
}) => {
  return AccountClient.query({
    query: gql`
      query ${querySource.fieldName}($input: ${querySource.argumentType}) {
        ${querySource.fieldName}(${querySource.argumentName}: $input)
      }
    `,
    variables: {
      input: additionalFilterConditions
        ? {
            joinOperator: 'AND',
            operands: (filterCondition?.operands ?? []).concat(
              additionalFilterConditions
            )
          }
        : filterCondition
    },
    fetchPolicy: 'network-only'
  });
};

export const Widget = ({
  entity,
  label,
  icon,
  metric,
  additionalFilterConditions,
  unitType
}: WidgetProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [aggregatedValue, setAggregatedValue] = useState<string | null>(null);

  const aggregateVariables = useMemo(() => {
    return {
      input: {
        entity: entity,
        metrics: [
          {
            ...metric,
            ...(metric.filterCondition
              ? {
                  filterCondition: {
                    joinOperator: 'AND',
                    operands: additionalFilterConditions
                      ? (metric?.filterCondition?.operands ?? []).concat(
                          additionalFilterConditions
                        )
                      : metric.filterCondition.operands
                  }
                }
              : {})
          }
        ]
      }
    };
  }, [entity, metric, additionalFilterConditions]);

  const [getAggregatedValues] = useLazyQuery(GET_AGGRGATED_VALUES, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted: ({ getAggregatedValues }) => {
      isLoading && setIsLoading(false);
      console.log('data', getAggregatedValues);
      setAggregatedValue(getAggregatedValues?.data?.[0]?.data?.[0]?.value);
    }
  });

  const fetchWidgets = () => {
    if (metric?.metricSource) {
      getDynamicWidgetQuery({
        querySource: metric?.metricSource,
        filterCondition: metric?.filterCondition,
        additionalFilterConditions: additionalFilterConditions ?? null
      })
        .then((res) => {
          isLoading && setIsLoading(false);
          setAggregatedValue(
            res.data[metric.metricSource.fieldName]?.toString()
          );
        })
        .catch(() => {
          isLoading && setIsLoading(false);
        });
    } else {
      getAggregatedValues({
        variables: aggregateVariables
      })
        .then((res) => {
          isLoading && setIsLoading(false);
        })
        .catch(() => {
          isLoading && setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchWidgets();
  }, []);

  useEffect(() => {
    const eventIndex = EventEmitterWithIndex.subscribe(
      'refetchWidgets',
      fetchWidgets
    );
    return () => {
      EventEmitterWithIndex.unsubscribe('refetchWidgets', eventIndex);
    };
  }, [entity, metric, additionalFilterConditions]);

  // const ICON_MAP = {
  //   'app.label.total.count': 'ChartModule',
  //   'app.label.contract.item.count': 'UserForm',
  //   'app.label.service.hours.count': 'LoggedProjectTime',
  //   'app.label.service.quantity.count': 'LoggedProjectTime',
  //   'app.label.product.count': 'PuzzlePiece',
  //   'app.label.total.revenue': 'BillableColor',
  //   'app.label.assigned.block.hour.count': 'HourGlassColor',
  //   'app.label.assigned.block.money.count': 'BillableHours'
  // };

  const showCurrency =
    label === 'app.label.total.revenue' || unitType === 'CURRENCY';

  return (
    <div className="fg1">
      <Card border="secondary" padding="sizeSmall" hideOnPrint>
        <div className="fx actr jctr w200 g10 h-40">
          <span className="fx actr jctr w-40">
            <SVGIcon name={icon || 'ChartModule'} size="xl" />
          </span>
          <div className="right fx fcol fw ellipsis">
            <p className="ellipsis">
              <FormattedMessage id={label} />
            </p>
            {isLoading ? (
              <Spinner size="small" />
            ) : (
              <div className="f-20 font-weight-600">
                {showCurrency ? (
                  <CurrencyText value={aggregatedValue || '0'} />
                ) : (
                  aggregatedValue || '0'
                )}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
